import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { Beaker, HelpCircle } from "lucide-react";
import React from "react";

export const ComplianceWaterQuality: React.FC<{ data: any }> = ({ data }) => {
  const getBadgeVariant = (status: any) => {
    switch (status) {
      case "green":
        return "outline";
      case "blue":
        return "outline";
      case "yellow":
        return "outline";
      default:
        return "outline";
    }
  };

  const getBadgeClassName = (color: any) => {
    switch (color) {
      case "green":
        return "text-green-600 border-green-600";
      case "blue":
        return "text-blue-600 border-blue-600";
      case "yellow":
        return "text-yellow-600 border-yellow-600";
      default:
        return "text-gray-600 border-gray-600";
    }
  };

  const getTooltipContent = (parameter: string) => {
    const tooltips: { [key: string]: string } = {
      Turbidity: "Measurement of water clarity",
      "Chlorine Residual": "Free chlorine concentration",
      "pH Level": "Acidity/alkalinity measure",
      Temperature: "Water temperature",
      "Total Coliforms": "Bacterial contamination indicator",
      "Water Hardness": "Calcium and magnesium concentration",
    };
    return tooltips[parameter] || "Water quality parameter";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex item-center gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Beaker className="h-5 w-5" />
              Compliance & Water Quality
            </CardTitle>
          </div>
          <div className="mt-1">
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  The most recent measurement for this water quality parameter
                  compared against its regulatory compliance target.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data?.complianceWaterQuality &&
        data.complianceWaterQuality.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
            {data.complianceWaterQuality.map((item) => (
              <div key={item.parameter} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">
                      {item.parameter || "N/A"}
                    </span>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-3 w-3 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          {getTooltipContent(item.paramName)}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Badge
                    variant={getBadgeVariant(item.deviationType)}
                    className={getBadgeClassName(item.deviationType)}
                  >
                    {item.deviationType || "N/A"}
                  </Badge>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {item.avgTotalReadings || "N/A"}
                </div>
                <div className="text-sm text-gray-600">
                  {item.unit || "N/A"}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Target: {`${item.avgMin ?? "NA"} - ${item.avgMax ?? "NA"}`}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-6">
            No compliance water quality data available.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
